<template>
  <PWAside
    :title="name"
    :step="step"
    :steps="[
      { label: $t('design') },
      { label: $t('settings') }
    ]"
  >
    <template #default>
      <div v-show="step === 0">
        <div :class="$style.actionSidebarItem">
          <ActionSidebarTypeTrigger
            :value="type"
            :is-subscribed="isSubscribed"
            :is-whatsapp-disabled="isWhatsappDisabled"
            @input="$emit('change-form', { key: 'type', value: $event })"
          />
        </div>
        <div
          v-if="['message', 'modal'].includes(type) && haveAllChannels"
          :class="$style.actionSidebarItem"
        >
          <ActionSidebarRedirect
            :value="redirect"
            @input="$emit('change-form', { key: 'redirect', value: $event })"
          />
        </div>
        <div
          v-if="['message', 'chat'].includes(type)"
          :class="$style.actionSidebarItem"
        >
          <ActionSidebarAvatar
            :without-toggle="type === 'chat'"
            :avatar-enabled="avatarEnabled"
            :avatar="avatar"
            @update-avatarEnabled="$emit('change-form', { key: 'avatarEnabled', value: $event })"
            @update-avatar="$emit('change-form', { key: 'avatar', value: $event })"
          />
        </div>
        <div
          v-if="type === 'chat'"
          :class="$style.actionSidebarItem"
        >
          <ActionSidebarChatMessage
            :operator-name="operatorName"
            :message-number1="messageNumber1"
            :message-number2="messageNumber2"
            @update-operatorName="$emit('change-form', { key: 'operatorName', value: $event })"
            @update-messageNumber1="$emit('change-form', { key: 'messageNumber1', value: $event })"
            @update-messageNumber2="$emit('change-form', { key: 'messageNumber2', value: $event })"
            @handle-validation="$emit('handle-validation', $event)"
          />
        </div>
        <div
          v-if="type === 'chat'"
          :class="$style.actionSidebarItem"
        >
          <ActionSidebarQuickAnswer
            :quick-answer-enabled="quickAnswerEnabled"
            :quick-answer-value="quickAnswerValue"
            @update-quickAnswerEnabled="$emit('change-form', { key: 'quickAnswerEnabled', value: $event })"
            @update-quickAnswerValue="$emit('change-form', { key: 'quickAnswerValue', value: $event })"
          />
        </div>
        <div
          v-if="type === 'message'"
          :class="$style.actionSidebarItem"
        >
          <ActionSidebarMessage
            :title="title"
            :text="text"
            @update-title="$emit('change-form', { key: 'title', value: $event })"
            @update-text="$emit('change-form', { key: 'text', value: $event })"
            @handle-validation="$emit('handle-validation', $event)"
          />
        </div>
        <div
          v-if="type === 'modal'"
          :class="$style.actionSidebarItem"
        >
          <ActionSidebarDesign
            :background-position="backgroundPosition"
            :background-image="backgroundImage"
            :logo-file="logoFile"
            @update-backgroundPosition="$emit('change-form', { key: 'backgroundPosition', value: $event })"
            @update-backgroundImage="$emit('change-form', { key: 'backgroundImage', value: $event })"
            @update-logoFile="$emit('change-form', { key: 'logoFile', value: $event })"
          />
        </div>
        <div
          v-if="type === 'modal'"
          :class="$style.actionSidebarItem"
        >
          <ActionSidebarText
            :redirect="redirect"
            :modal-text="text"
            :extra-text-enabled="extraTextEnabled"
            :whatsapp-button-text="whatsappButtonText"
            :whatsapp-qrcode-text="whatsappQrcodeText"
            :telegram-button-text="telegramButtonText"
            :telegram-qrcode-text="telegramQrcodeText"
            @update-modalText="$emit('change-form', { key: 'text', value: $event })"
            @update-extraTextEnabled="$emit('change-form', { key: 'extraTextEnabled', value: $event })"
            @update-whatsappButtonText="$emit('change-form', { key: 'whatsappButtonText', value: $event })"
            @update-whatsappQrcodeText="$emit('change-form', { key: 'whatsappQrcodeText', value: $event })"
            @update-telegramButtonText="$emit('change-form', { key: 'telegramButtonText', value: $event })"
            @update-telegramQrcodeText="$emit('change-form', { key: 'telegramQrcodeText', value: $event })"
            @handle-validation="$emit('handle-validation', $event)"
          />
        </div>
        <div
          v-if="['message', 'modal'].includes(type)"
          :class="$style.actionSidebarItem"
        >
          <ActionSidebarDarkMode
            :value="isDark"
            @input="$emit('change-form', { key: 'isDark', value: $event })"
          />
        </div>
        <div
          v-if="isSubscribed"
          :class="$style.actionSidebarItem"
        >
          <ActionSidebarBranding
            :value="branding"
            @input="$emit('change-form', { key: 'branding', value: $event })"
          />
        </div>
      </div>
      <div v-show="step === 1">
        <div :class="$style.actionSidebarItem">
          <ActionSidebarSettingsQuery
            :value="queryMode"
            @input="$emit('change-query', { key: 'queryMode', value: $event })"
          />
        </div>
        <div :class="$style.actionSidebarItem">
          <ActionSidebarSettingsTriggers
            :after-page-load-enabled="afterPageLoadEnabled"
            :after-page-load-seconds="afterPageLoadSeconds"
            :page-scroll-below-enabled="pageScrollBelowEnabled"
            :page-scroll-below-amount="pageScrollBelowAmount"
            :device-enabled="deviceEnabled"
            :device-type="deviceType"
            :location-enabled="locationEnabled"
            :location-matcher="locationMatcher"
            :location-values="locationValues"
            @update-afterPageLoadEnabled="$emit('change-query', { key: 'afterPageLoadEnabled', value: $event })"
            @update-afterPageLoadSeconds="$emit('change-query', { key: 'afterPageLoadSeconds', value: $event })"
            @update-pageScrollBelowEnabled="$emit('change-query', { key: 'pageScrollBelowEnabled', value: $event })"
            @update-pageScrollBelowAmount="$emit('change-query', { key: 'pageScrollBelowAmount', value: $event })"
            @update-deviceEnabled="$emit('change-query', { key: 'deviceEnabled', value: $event })"
            @update-deviceType="$emit('change-query', { key: 'deviceType', value: $event })"
            @update-locationEnabled="$emit('change-query', { key: 'locationEnabled', value: $event })"
            @update-locationMatcher="$emit('change-query', { key: 'locationMatcher', value: $event })"
            @update-locationValues="$emit('change-query', { key: 'locationValues', value: $event })"
          />
        </div>
      </div>
    </template>
    <template #footer>
      <div
        v-if="step === 0"
        :class="$style.actionSidebarFooter"
      >
        <PWButton
          size="medium"
          :disabled="disabled"
          @click="nextStep"
        >
          <span>{{ $t('next') }}</span>
          <ChevronRightRounded :class="$style.actionSidebarFooterIcon" />
        </PWButton>
      </div>
      <div
        v-else
        :class="$style.actionSidebarFooter"
      >
        <PWButton
          size="medium"
          :disabled="disabled"
          @click="$emit('submit')"
        >
          {{ $t('save') }}
        </PWButton>
        <PWButton
          variant="outline"
          size="medium"
          :disabled="disabled"
          @click="prevStep"
        >
          <ChevronLeftRounded :class="$style.actionSidebarFooterIcon" />
          <span>{{ $t('back') }}</span>
        </PWButton>
      </div>
    </template>
  </PWAside>
</template>

<script>
/**
 * Utils
 */
import mixinForProps from '@/components/Actions/_mixin-for-props.ts'
/**
 * UI
 */
import PWAside from '@/components/_ui/Sidebars/PWAside.vue'
import PWButton from '@/components/_ui/Buttons/PWButton.vue'
import ActionSidebarTypeTrigger from '@/components/Actions/ActionSidebar/ActionSidebarTypeTrigger.vue'
import ActionSidebarRedirect from '@/components/Actions/ActionSidebar/ActionSidebarRedirect.vue'
import ActionSidebarDesign from '@/components/Actions/ActionSidebar/ActionSidebarDesign/ActionSidebarDesign.vue'
import ActionSidebarText from '@/components/Actions/ActionSidebar/ActionSidebarText.vue'
import ActionSidebarDarkMode from '@/components/Actions/ActionSidebar/ActionSidebarDarkMode.vue'
import ActionSidebarBranding from '@/components/Actions/ActionSidebar/ActionSidebarBranding.vue'
import ActionSidebarSettingsQuery from '@/components/Actions/ActionSidebar/ActionSidebarSettingsQuery.vue'
import ActionSidebarSettingsTriggers from '@/components/Actions/ActionSidebar/ActionSidebarSettingsTriggers/ActionSidebarSettingsTriggers.vue'
import ActionSidebarMessage from '@/components/Actions/ActionSidebar/ActionSidebarMessage.vue'
import ActionSidebarAvatar from '@/components/Actions/ActionSidebar/ActionSidebarAvatar/ActionSidebarAvatar.vue'
import ActionSidebarChatMessage from '@/components/Actions/ActionSidebar/ActionSidebarChatMessage.vue'
import ActionSidebarQuickAnswer from '@/components/Actions/ActionSidebar/ActionSidebarQuickAnswer.vue'
/**
 * Static
 */
import ChevronLeftRounded from '@/assets/icons/ChevronLeftRounded.svg?inline'
import ChevronRightRounded from '@/assets/icons/ChevronRightRounded.svg?inline'

export default {
  components: {
    PWAside,
    PWButton,
    ChevronLeftRounded,
    ChevronRightRounded,
    ActionSidebarTypeTrigger,
    ActionSidebarRedirect,
    ActionSidebarDesign,
    ActionSidebarText,
    ActionSidebarDarkMode,
    ActionSidebarBranding,
    ActionSidebarSettingsQuery,
    ActionSidebarSettingsTriggers,
    ActionSidebarMessage,
    ActionSidebarAvatar,
    ActionSidebarChatMessage,
    ActionSidebarQuickAnswer
  },
  mixins: [mixinForProps],
  props: {
    disabled: {
      type: Boolean,
      default: false
    },
    isSubscribed: {
      type: Boolean,
      default: true
    },
    haveAllChannels: {
      type: Boolean,
      default: true
    },
    isWhatsappDisabled: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    step: 0
  }),
  methods: {
    nextStep () {
      this.step = 1
    },
    prevStep () {
      this.step = 0
    }
  }
}
</script>

<style module lang="scss">
.actionSidebarFooter {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 16px;
}

.actionSidebarFooterIcon {
  --s: 20px;

  width: var(--s);
  height: var(--s);
}

.actionSidebarItem + .actionSidebarItem {
  border-top: 1px solid var(--grey-200);
}
</style>
